import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import routeService from "@plannel/services/data/route-service"
import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { PRODUCTION_PRIORITY } = CodeUtil.priorityItems;
const { FAST, PRIORITY } = PRODUCTION_PRIORITY;

const ALT_RES_POLICY_OPTIONS = [FAST, PRIORITY]
const Route = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [routeDropdownList, setRouteDropdownList] = useState([]);

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [routeSearch, setRouteSearch] = useState(reduxViewState?.routeSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [route, setRoute] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setItemCode = function (params) {
    params.data.item = { itemCd: params.newValue, name: "" };
    return true;
  }

  const altResPolicyValueFormatter = (params) => {
    if (isEmpty(params?.value)) {
      return "";
    }
    const { value } = params;

    return t(value);
  }

  const columnDefs = [
    { headerName: "routeCd", field: "routeCd", colId: "routeCd", cellClass: 'stringType', filterType: "string" },
    { headerName: "routeName", field: "name", colId: "routeName", filterType: "string" },
    { headerName: "itemCd", field: "item.itemCd", colId: "itemCd", valueSetter: setItemCode, minWidth: 150, flex: 2, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "item.name", colId: "itemName", type: ["nonEditableColumn"], minWidth: 300, flex: 5, filterType: "string" },
    { headerName: "discreteFlg", field: "discreteFlg", type: ["booleanColumn"], width: 130, filterType: "boolean" },
    { headerName: "divisibleFlg", field: "divisibleFlg", type: ["booleanColumn"], width: 130, filterType: "boolean" },
    { 
      headerName: "altResPolicyCd", field: "altResPolicyCd", width: 150,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: altResPolicyValueFormatter,
      cellEditorParams: {
        valueFormatter: altResPolicyValueFormatter,
        values: ALT_RES_POLICY_OPTIONS
      },
      cellClass: 'stringType',
      filterType: "string" ,
      filterOption: ALT_RES_POLICY_OPTIONS
    },
    { headerName: "priority", field: "priority", type: ["numberColumn", "rightAligned"], width: 150, filterType: "number" },
    { headerName: "allocRatio", field: "allocRatio", type: ["numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "maxEarlyProdDays", field: "maxEarlyProdDays", type: ["numberColumn", "rightAligned"], width: 140, filterType: "number" },
    { headerName: "descTxt", field: "descTxt", width: 280, filterType: "string" },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: "boolean" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (routeSearch) {
      searchFilters.routeCd = routeSearch.routeCd;
    }

    if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }
    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveRouteDropdownList = () => {
    routeService.getLookup()
      .then(response => {
        const { data } = response;
        if (isEmpty(data)) {
          setRouteDropdownList([]);
        } else {
          setRouteDropdownList(data);
        }
      })
      .catch(e => {
        debug(e);
      })
  }

  const retrieveRoute = () => {
    const params = getRequestParams();
    routeService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setRoute([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setRoute(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { routeSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const insertRow = () => {
    const newRow = {
      id: null,
      routeCd: "",
      routeName: null,
      itemCd: "",
      itemName: "",
      priority: 999,
      maxEarlyProdDays: null,
      descText: null,
      discreteFlg: true,
      divisibleFlg: false,
      altResPolicyCd: FAST,
      activeFlg: true,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removeRoute = () => {
    const routeIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        routeIds.push(data.id)
      }
    );
    if (routeIds.length > 0) {
      routeService.remove(routeIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveRoute();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveRoute = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      routeService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          setProgressOn(false);
          retrieveRoute();
        });
    } else {
      setProgressOn(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      retrieveRoute();
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setProgressOn(true)
      saveRoute();
    } else if (action === "DELETE") {
      removeRoute();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveRoute();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveRoute();
    }

    return () => {
      setRoute([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    return () => {
      setRoute([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSearch, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(route)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  /* Dropdown List 데이터 셋팅 */
  useEffect(() => {
    retrieveRouteDropdownList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Autocomplete
          options={routeDropdownList}
          getOptionLabel={option => option.name}
          value={routeSearch}
          onChange={(_, newValue) => setRouteSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.routeCd === value.routeCd}
          size="small"
          sx={{ my: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("route")} />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} sx={{ ml: 1.5, my: 1 }} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ mt: 1.5, ml: "auto", mr: 1.5 }}>
          <ExcelExportButton title={title} service={routeService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.route, 'delete')}` })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </Box>
      <Box id="production-route-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={route}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Route);